import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAllSowingInstructions } from "../../hooks/useSowingInstructions";
import { useReparentSowing } from "../../hooks/useSowings";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import type { Sowing } from "../../services/sowingsApi";
import { SowingContextBlock } from "./SowingContextBlock";

interface ReparentSowingDialogProps {
  sowing: Sowing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const ReparentSowingDialog = ({
  sowing,
  open,
  onOpenChange,
  onSuccess,
}: ReparentSowingDialogProps) => {
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setTargetId("");
      setReason("");
    }
  }, [open]);

  const instructionsQuery = useAllSowingInstructions();
  const reparent = useReparentSowing();
  const confirm = useConfirm();
  const notify = useNotification();

  // Server enforces seed source / crop / variety / customer match. We narrow
  // the dropdown to the obvious-no-match cases (same instruction, revoked) so
  // the manager's first pick has a chance of succeeding.
  const candidates = (instructionsQuery.data ?? []).filter((si: any) => {
    if (si.id === sowing.sowingInstructionId) return false;
    if (si.isRevoked) return false;
    return true;
  });

  const handleSubmit = () => {
    if (!targetId) {
      notify.error("Select a target sowing instruction.");
      return;
    }
    if (!reason.trim()) {
      notify.error("Reason is required to reparent.");
      return;
    }

    confirm({
      title: "Reparent sowing?",
      description: "This sowing will be moved to the selected sowing instruction.",
      confirmLabel: "Reparent",
      onConfirm: () =>
        reparent
          .mutateAsync({
            sowingId: sowing.id,
            originalSowingInstructionId: sowing.sowingInstructionId,
            payload: { newSowingInstructionId: targetId, reason },
          })
          .then(() => {
            notify.success("Sowing reparented.");
            onSuccess?.();
            onOpenChange(false);
          })
          .catch((error: any) => {
            notify.error(
              error.response?.data?.message || "Failed to reparent sowing."
            );
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Re-parent Sowing</DialogTitle>
          <DialogDescription>
            Move this sowing record to a different sowing instruction. Target
            must match seed source, crop, variety, and customer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <SowingContextBlock sowing={sowing} />

          <Field>
            <FieldLabel>
              Target sowing instruction{" "}
              <span className="text-red-500">*</span>
            </FieldLabel>
            <Select
              value={targetId}
              onValueChange={setTargetId}
              disabled={reparent.isPending || instructionsQuery.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select instruction" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((si: any) => (
                  <SelectItem key={si.id} value={si.id}>
                    {si.instructionNumber ?? si.id} — {si.variety ?? "?"} ·{" "}
                    {si.quantityToSow ?? "?"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>
              Reason <span className="text-red-500">*</span>
            </FieldLabel>
            <Textarea
              placeholder="Why move this sowing?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={reparent.isPending}
              maxLength={1000}
            />
          </Field>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={reparent.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                reparent.isPending || !targetId || !reason.trim()
              }
            >
              {reparent.isPending ? "Moving..." : "Re-parent"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
